import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TripForm } from '../components/TripForm.jsx';
import { Itineraire } from '../components/Itineraire.jsx';
import { Chronologie } from '../components/Chronologie.jsx';
import { Carte } from '../components/Carte.jsx';
import { PanneauPartage } from '../components/PanneauPartage.jsx';
import {
  chargerVoyage,
  modifierVoyage,
  supprimerVoyage,
  lancerGeneration,
  creerItineraireVide,
  changerStatut,
  STATUTS,
} from '../lib/trips.js';
import {
  decrireParticipant,
  formaterMontant,
  formaterDate,
  resumerPeriode,
} from '../lib/format.js';

const ONGLETS = [
  ['detail', 'Détail'],
  ['itineraire', 'Itinéraire'],
  ['partage', 'Partage'],
];

/** Résumé d'une ligne sous le titre : ce qu'on veut savoir sans lire la fiche. */
function resumer(voyage) {
  const budget = formaterMontant(voyage.budget.amount, voyage.budget.currency);
  return [
    resumerPeriode(voyage),
    voyage.participants.length > 0
      ? `${voyage.participants.length} personne${voyage.participants.length > 1 ? 's' : ''}`
      : null,
    budget ? `Budget indicatif ${budget}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function Fiche({ libelle, valeur, precision, large = false }) {
  if (!valeur) return null;
  return (
    <div className={`fiche${large ? ' fiche--large' : ''}`}>
      <dt>{libelle}</dt>
      <dd>
        {valeur}
        {precision && <small>{precision}</small>}
      </dd>
    </div>
  );
}

export function TripView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [voyage, setVoyage] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [onglet, setOnglet] = useState('detail');
  const [edition, setEdition] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [erreurGeneration, setErreurGeneration] = useState(null);
  const [erreurStatut, setErreurStatut] = useState(null);
  // Deux lectures du même itinéraire : par jour pour travailler une journée,
  // chronologie pour juger le rythme d'ensemble.
  const [vue, setVue] = useState('jour');

  const generationEnCours = voyage?.generation.status === 'running';
  // Une régénération ciblée laisse le reste du programme lisible : seule la
  // journée concernée montre son attente.
  const generationGlobale = generationEnCours && voyage?.generation.dayIndex === null;

  useEffect(() => {
    let annule = false;

    chargerVoyage(id)
      .then((charge) => {
        if (annule) return;
        setVoyage(charge);
        // On ouvre sur l'itinéraire quand il existe : c'est ce qu'on vient
        // consulter une fois le voyage préparé.
        if (charge.itinerary.mode) setOnglet('itineraire');
      })
      .catch((echec) => !annule && setErreur(echec.message));

    return () => {
      annule = true;
    };
  }, [id]);

  // Pendant la génération, le serveur travaille en arrière-plan : on relit le
  // voyage régulièrement jusqu'à ce que son état change. Trois secondes est un
  // compromis — assez vif pour ne pas paraître figé, assez espacé pour ne pas
  // marteler l'API pendant les deux ou trois minutes que dure l'attente.
  useEffect(() => {
    if (!generationEnCours) return undefined;

    const minuterie = setInterval(async () => {
      try {
        setVoyage(await chargerVoyage(id));
      } catch {
        // Une lecture ratée n'a rien de définitif : la suivante réessaiera.
      }
    }, 3000);

    return () => clearInterval(minuterie);
  }, [generationEnCours, id]);

  if (erreur) {
    return (
      <main className="page page--etroite">
        <section className="verre">
          <h1>Voyage introuvable</h1>
          <p>{erreur}</p>
          <p>
            <Link to="/voyages">← Mes voyages</Link>
          </p>
        </section>
      </main>
    );
  }

  if (!voyage) {
    return (
      <main className="page page--etroite">
        <p role="status">Chargement…</p>
      </main>
    );
  }

  if (edition) {
    return (
      <main className="page">
        <section className="verre">
          <p className="fil">
            <Link to="/voyages">← Mes voyages</Link>
          </p>
          <h1>Modifier le voyage</h1>
          <TripForm
            voyage={voyage}
            libelleAction="Enregistrer"
            onSubmit={async (donnees) => {
              setVoyage(await modifierVoyage(voyage.id, donnees));
              setEdition(false);
            }}
            onCancel={() => setEdition(false)}
          />
        </section>
      </main>
    );
  }

  const budget = formaterMontant(voyage.budget.amount, voyage.budget.currency);
  const dates =
    voyage.dateMode === 'fixed_dates' && voyage.startDate
      ? `${[formaterDate(voyage.startDate), voyage.startTime].filter(Boolean).join(' à ')} → `
        + `${[formaterDate(voyage.endDate), voyage.endTime].filter(Boolean).join(' à ')}`
      : null;

  return (
    <main className="page">
      <p className="fil">
        <Link to="/voyages">← Mes voyages</Link>
      </p>

      <header>
        <h1>{voyage.name}</h1>
        <p className="entete__sous-titre">{resumer(voyage)}</p>
      </header>

      <ul className="onglets">
        {ONGLETS.map(([cle, libelle]) => (
          <li key={cle}>
            <button
              type="button"
              className={`pilule${onglet === cle ? ' pilule--active' : ''}`}
              aria-current={onglet === cle ? 'page' : undefined}
              onClick={() => setOnglet(cle)}
            >
              {libelle}
            </button>
          </li>
        ))}
      </ul>

      {onglet === 'detail' && (
        <>
          <dl className="fiches">
            <Fiche libelle="Destination" valeur={voyage.destination.label} />
            <Fiche libelle="Ville de départ" valeur={voyage.origin} />
            <Fiche
              libelle="Dates"
              valeur={dates ?? resumerPeriode(voyage)}
              precision={dates ? `${voyage.durationDays} jours` : null}
            />
            <Fiche libelle="Budget indicatif" valeur={budget} />
            <Fiche
              libelle="Type de voyage"
              valeur={voyage.tripTypes.length > 0 ? voyage.tripTypes.join(' · ') : null}
              precision={voyage.tripTypeNotes}
            />
            {/* Toujours affichée, même vide : masquer la fiche rendait
                l'absence de voyageurs invisible, alors que c'est justement ce
                qu'il faut corriger. */}
            <div className="fiche fiche--large">
              <dt>{voyage.participants.length > 1 ? 'Voyageurs' : 'Voyageur'}</dt>
              <dd>
                {voyage.participants.length > 0 ? (
                  <ul className="liste-simple">
                    {voyage.participants.map((participant, index) => (
                      <li key={index}>{decrireParticipant(participant, index)}</li>
                    ))}
                  </ul>
                ) : (
                  <small>Aucun voyageur renseigné — à ajouter en modifiant le voyage.</small>
                )}
              </dd>
            </div>
            <Fiche libelle="Contraintes libres" valeur={voyage.groupDescription} large />
            <Fiche
              libelle="Itinéraire"
              valeur={voyage.itinerary.mode ? 'Établi' : 'Pas encore établi'}
              large
            />
          </dl>

          <section className="panneau">
            <h2>Où en est ce voyage ?</h2>
            <ul className="onglets">
              {STATUTS.map(([cle, libelle]) => (
                <li key={cle}>
                  <button
                    type="button"
                    className={`pilule${voyage.status === cle ? ' pilule--active' : ''}`}
                    aria-pressed={voyage.status === cle}
                    onClick={async () => {
                      setErreurStatut(null);
                      try {
                        setVoyage(await changerStatut(voyage.id, cle));
                      } catch (echec) {
                        setErreurStatut(echec.message);
                      }
                    }}
                  >
                    {libelle}
                  </button>
                </li>
              ))}
            </ul>
            <p className="aide">
              Les voyages archivés passent en fin de liste sur le tableau de bord.
            </p>
            {erreurStatut && (
              <p className="erreur" role="alert">
                {erreurStatut}
              </p>
            )}
          </section>

          <div className="actions actions--pied">
            <button className="bouton bouton--discret" type="button" onClick={() => setEdition(true)}>
              Modifier le voyage
            </button>
            <button
              className="bouton bouton--discret"
              type="button"
              onClick={() => setConfirmation(true)}
            >
              Supprimer
            </button>
          </div>

          {confirmation && (
            <div className="confirmation" role="alertdialog" aria-label="Confirmer la suppression">
              <p>Supprimer « {voyage.name} » ? Cette action est définitive.</p>
              <div className="actions">
                <button
                  className="bouton bouton--danger"
                  type="button"
                  onClick={async () => {
                    await supprimerVoyage(voyage.id);
                    navigate('/voyages', { replace: true });
                  }}
                >
                  Supprimer définitivement
                </button>
                <button
                  className="bouton bouton--discret"
                  type="button"
                  onClick={() => setConfirmation(false)}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {onglet === 'itineraire' && (
        <>
          {generationGlobale ? (
            <p className="patiente" role="status">
              <span className="patiente__point" aria-hidden="true" />
              Génération de l’itinéraire en cours… Comptez une à trois minutes.
              Vous pouvez quitter cette page et revenir plus tard.
            </p>
          ) : (
            <div className="actions">
              <button
                className="bouton bouton--auto"
                type="button"
                disabled={generationEnCours}
                onClick={async () => {
                  setErreurGeneration(null);
                  try {
                    setVoyage(await lancerGeneration(voyage.id));
                  } catch (echec) {
                    setErreurGeneration(echec.message);
                  }
                }}
              >
                {voyage.itinerary.mode ? 'Régénérer l’itinéraire' : 'Générer l’itinéraire'}
              </button>

              {/* Sans cette porte de sortie, un voyage reste un formulaire mort
                  tant qu'aucune génération n'a abouti. */}
              {!voyage.itinerary.mode && (
                <button
                  className="bouton bouton--discret bouton--auto"
                  type="button"
                  onClick={async () => {
                    setErreurGeneration(null);
                    try {
                      setVoyage(await creerItineraireVide(voyage.id));
                    } catch (echec) {
                      setErreurGeneration(echec.message);
                    }
                  }}
                >
                  Le composer moi-même
                </button>
              )}
            </div>
          )}

          {(erreurGeneration || voyage.generation.error) && !generationEnCours && (
            <p className="erreur" role="alert">
              {erreurGeneration ?? `La génération a échoué : ${voyage.generation.error}`}
            </p>
          )}

          {voyage.itinerary.mode === 'detailed' && !generationGlobale && (
            <ul className="onglets onglets--vues">
              {[['jour', 'Par jour'], ['chronologie', 'Chronologie'], ['carte', 'Carte']].map(([cle, libelle]) => (
                <li key={cle}>
                  <button
                    type="button"
                    className={`pilule${vue === cle ? ' pilule--active' : ''}`}
                    aria-pressed={vue === cle}
                    onClick={() => setVue(cle)}
                  >
                    {libelle}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {voyage.itinerary.mode && !generationGlobale && (
            vue === 'chronologie' && voyage.itinerary.mode === 'detailed' ? (
              <Chronologie itineraire={voyage.itinerary} />
            ) : vue === 'carte' && voyage.itinerary.mode === 'detailed' ? (
              <Carte itineraire={voyage.itinerary} tripId={voyage.id} onChange={setVoyage} />
            ) : (
              <Itineraire
                itineraire={voyage.itinerary}
                tripId={voyage.id}
                onChange={setVoyage}
                journeeEnGeneration={generationEnCours ? voyage.generation.dayIndex : null}
              />
            )
          )}
        </>
      )}

      {onglet === 'partage' && (
        <>
          {voyage.itinerary.mode ? (
            <>
              <section className="panneau">
                <h2>Emporter l’itinéraire</h2>
                <p className="aide aide--haut">
                  Un document à imprimer ou à garder sur son téléphone, consultable
                  sans réseau.
                </p>
                {/* Navigation réelle et non requête : le navigateur reçoit le
                    fichier et le cookie de session accompagne la demande. */}
                <a className="bouton bouton--lien" href={`/api/trips/${voyage.id}/export/pdf`}>
                  Télécharger le PDF
                </a>
              </section>

              <PanneauPartage tripId={voyage.id} />
            </>
          ) : (
            <p className="aide">
              L’itinéraire n’existe pas encore : rien à exporter ni à partager pour
              l’instant.
            </p>
          )}
        </>
      )}
    </main>
  );
}
